import React from 'react';
import PropTypes from 'prop-types';
import BookmarkIcon from '../Icons/BookmarkIcon';
import Label from '../Label/Label';
import Image from '../Image/Image';
import PinIcon from '../Icons/PinIcon';
import { cc } from '../../modules/styles';
import Icon from '../Icons/Icon';

import './PostItem.css';

/**
 * Item for the list of Posts in the main view
 */

const PostItem = ({
  id,
  title,
  subreddit,
  domain,
  thumbnail = null,
  nsfw = false,
  stickied = false,
  label,
  saved = false,
  likes,
  previewImage,
  galleryImages,
  isSelected,
  bigThumbnail = true,
  blurNSFW = true,
  galleryItemsAmount = null,
  openPostRequest,
}) => {
  const postUrl = `#post:${id}`;
  const galleryPreview = galleryImages && galleryImages[0];
  const imagePreview = galleryPreview
    ? {
        url: galleryPreview.url,
        width: galleryPreview.w,
        height: galleryPreview.h,
      }
    : previewImage;
  const showBigThumbnail = bigThumbnail && imagePreview;
  const smallThumbnailSize = 70;
  const isGallery = galleryItemsAmount !== null && galleryItemsAmount > 0;

  return (
    <article
      className={cc(
        'PostItem',
        isSelected ? 'PostItem--selected' : '',
        stickied ? 'PostItem--stickied' : '',
        bigThumbnail ? 'PostItem--big' : ''
      )}
      style={{ '--image-size': `${smallThumbnailSize}px` }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: showBigThumbnail ? 'column' : undefined,
        }}
      >
        {(thumbnail || showBigThumbnail) && (
          <div
            className={cc(
              'PostItem__thumbnail-wrap',
              showBigThumbnail ? 'PostItem__thumbnail-wrap--big' : '',
              nsfw && blurNSFW ? 'PostItem__warning-wrap' : ''
            )}
            style={{
              flex: !showBigThumbnail
                ? `0 0 ${smallThumbnailSize}px`
                : undefined,
              marginRight: showBigThumbnail ? undefined : '0.75rem',
            }}
          >
            <a
              href={postUrl}
              className="PostItem__image-wrap"
              onClick={ev => {
                ev.preventDefault();
                openPostRequest();
              }}
              tabIndex={-1}
            >
              <Image
                url={showBigThumbnail ? imagePreview.url : thumbnail}
                description={title}
                className={cc(
                  'PostItem__image',
                  !showBigThumbnail ? 'PostItem__image--big' : ''
                )}
                width={
                  showBigThumbnail ? imagePreview.width : smallThumbnailSize
                }
                height={
                  showBigThumbnail ? imagePreview.height : smallThumbnailSize
                }
              />
              {isGallery && (
                <p className="PostItem__gallery-count">
                  <span aria-hidden>+{galleryItemsAmount - 1}</span>
                  <span className="visually-hide">
                    Contains a gallery of {galleryItemsAmount} images.
                  </span>
                </p>
              )}
            </a>
          </div>
        )}

        <div style={{ width: '100%' }}>
          <a
            href={postUrl}
            className="PostItem__title"
            onClick={ev => {
              ev.preventDefault();
              openPostRequest();
            }}
          >
            {stickied && <PinIcon className="PostItem__pin-icon" size="1em" />}
            {title}
          </a>
          <div className="PostItem__data">
            <div className="PostItem__domain">{domain}</div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <div
                className="PostItem__subreddit"
                style={{
                  '--theme-color-dark': '35%',
                  '--theme-color-light': '62%',
                }}
              >
                {subreddit}
              </div>
              {label && (
                <div style={{ marginRight: '0.25rem' }}>
                  <Label text={label} type="basic" />
                </div>
              )}
              {nsfw && (
                <div>
                  <Label text="nsfw" type="warning" />
                </div>
              )}
              {(saved || likes != null) && (
                <div
                  style={{
                    '--theme-color-dark': '35%',
                    '--theme-color-light': '62%',
                    marginLeft: 'auto',
                  }}
                >
                  {saved && <BookmarkIcon size="1em" />}
                  {likes != null && (
                    <Icon
                      name="triangle"
                      size="0.9em"
                      rotation={likes ? null : 180}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

PostItem.propTypes = {
  title: PropTypes.string.isRequired,
  subreddit: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
  isSelf: PropTypes.bool.isRequired,
  thumbnail: PropTypes.string,
  nsfw: PropTypes.bool,
  stickied: PropTypes.bool,
  saved: PropTypes.bool,
  likes: PropTypes.bool,
  previewImage: PropTypes.object,
  openPostRequest: PropTypes.func.isRequired,
};

export default PostItem;
